import React, { useState, useEffect } from "react";
import { Button, Card, CardContent, Divider, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Settings as SettingsIcon,
} from "@material-ui/icons";
import { head, isArray } from "lodash";
import { toast } from "react-toastify";
import moment from "moment";
import { listUsersSimple } from "../../services/users.api";
import { listTimezones, getTimezone } from "../../services/settings.api";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import { i18n } from "../../translate/i18n";
import { useStyles } from "./CompaniesManagerImprovedStyles";

// D8.6.3 — Formulario de empresa (crear/editar) extraído del god file.

export default function CompanyFormImproved(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});
  const [timezones, setTimezones] = useState([]); // ✅ DB-FIRST: Lista dinámica de timezones
  const [loadingTimezones, setLoadingTimezones] = useState(true);

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    timezone: "", // ✅ Inicializar vacío para evitar warning de Material-UI
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ DB-FIRST: Cargar timezones dinámicamente desde la base de datos
  useEffect(() => {
    const loadTimezones = async () => {
      try {
        setLoadingTimezones(true);
        const { data } = await listTimezones();
        if (data && data.success && data.data) {
          setTimezones(data.data);
          
          // ✅ CORRECCIÓN: Establecer timezone solo después de cargar timezones
          // Si el record tiene timezone vacío, usar el default
          if (!record.timezone || record.timezone === "") {
            const defaultTimezone = "America/Lima";
            // Verificar que el timezone existe en la lista cargada
            const exists = data.data.some(tz => tz.timezone === defaultTimezone);
            if (exists) {
              setRecord(prev => ({ ...prev, timezone: defaultTimezone }));
            }
          }
        } else {
          // Fallback: Si falla, usar lista hardcodeada mínima
          console.warn('No se pudieron cargar timezones desde BD, usando fallback');
          setTimezones([
            { timezone: 'America/Lima', displayName: 'Perú (America/Lima)', region: 'America' }
          ]);
          if (!record.timezone || record.timezone === "") {
            setRecord(prev => ({ ...prev, timezone: "America/Lima" }));
          }
        }
      } catch (error) {
        console.error('Error cargando timezones:', error);
        // Fallback: Lista mínima
        setTimezones([
          { timezone: 'America/Lima', displayName: 'Perú (America/Lima)', region: 'America' }
        ]);
        if (!record.timezone || record.timezone === "") {
          setRecord(prev => ({ ...prev, timezone: "America/Lima" }));
        }
      } finally {
        setLoadingTimezones(false);
      }
    };
    
    loadTimezones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadCompanyData = async () => {
      const newRecord = { ...record, ...initialValue };
      
      // ✅ FORMATEAR FECHA DE VENCIMIENTO SI EXISTE Y ES VÁLIDA
      if (initialValue.dueDate) {
        const momentDate = moment(initialValue.dueDate);
        if (momentDate.isValid()) {
          newRecord.dueDate = momentDate.format("YYYY-MM-DD");
        }
      }
      
      // ✅ CARGAR TIMEZONE DESDE SETTINGS (si estamos editando una empresa existente)
      // ✅ PRIORIDAD: Settings.timezone > Companies.timezone > Default
      // ✅ CORRECCIÓN: Asegurar que siempre se establezca un timezone válido
      if (initialValue.id) {
        try {
          const { data } = await getTimezone({
            params: { companyId: initialValue.id }
          });
          if (data && data.value) {
            newRecord.timezone = data.value;
            console.log(`[CompanyForm] Timezone cargado desde Settings: ${data.value}`);
          } else {
            // ✅ FALLBACK 1: Si Settings no tiene valor, usar Companies.timezone
            if (initialValue.timezone) {
              newRecord.timezone = initialValue.timezone;
              console.log(`[CompanyForm] Timezone cargado desde Companies (Settings vacío): ${initialValue.timezone}`);
            } else {
              // Si tampoco hay en Companies, usar default
              newRecord.timezone = "America/Lima";
              console.log(`[CompanyForm] Timezone no encontrado, usando default: America/Lima`);
            }
          }
        } catch (error) {
          // ✅ FALLBACK 2: Si Settings falla (404, etc), usar Companies.timezone
          // ✅ 404 es esperado si Settings no tiene timezone configurado aún
          if (initialValue.timezone) {
            newRecord.timezone = initialValue.timezone;
            console.log(`[CompanyForm] Timezone cargado desde Companies (Settings no configurado): ${initialValue.timezone}`);
          } else {
            // Si tampoco hay en Companies, usar default
            newRecord.timezone = "America/Lima";
            if (error.response?.status !== 404) {
              // Solo mostrar warning si NO es un 404 (404 es esperado)
              console.warn(`[CompanyForm] No se pudo cargar timezone desde Settings ni Companies, usando default. Error:`, error.message || error);
            }
          }
        }
      } else if (initialValue.timezone) {
        // ✅ Si es nueva empresa pero tiene timezone en initialValue, usarlo
        newRecord.timezone = initialValue.timezone;
        console.log(`[CompanyForm] Timezone establecido desde initialValue: ${initialValue.timezone}`);
      } else {
        // ✅ Si no hay timezone en ningún lado, usar default
        newRecord.timezone = "America/Lima";
      }
      
      // ✅ Asegurar que planId sea string para evitar warning de Material-UI
      if (newRecord.planId && typeof newRecord.planId !== 'string') {
        newRecord.planId = String(newRecord.planId);
      }
      
      // ✅ Si planId no existe en la lista de planes disponibles, limpiarlo
      if (newRecord.planId && plans.length > 0) {
        const planExists = plans.some(p => String(p.id) === String(newRecord.planId));
        if (!planExists) {
          console.warn(`⚠️ Plan ${newRecord.planId} no existe en la lista de planes disponibles, limpiando...`);
          newRecord.planId = "";
        }
      }
      
      setRecord(newRecord);
    };
    
    loadCompanyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, plans]);

  // ✅ OPTIMIZADO: Recargar timezone cuando cambia record.id o cuando se detecta actualización
  // ✅ NOTA: Este useEffect es complementario a handleSubmit, que ya maneja la recarga principal
  useEffect(() => {
    const reloadTimezone = async () => {
      // Solo recargar si estamos editando una empresa existente (no nueva)
      if (record.id && record.id === initialValue.id && initialValue.id) {
        try {
          const { data } = await getTimezone({
            params: { companyId: record.id }
          });
          if (data && data.value && data.value !== record.timezone) {
            console.log(`🔄 [CompanyForm] Recargando timezone desde Settings (useEffect): ${data.value}`);
            setRecord((prev) => ({
              ...prev,
              timezone: data.value
            }));
          }
        } catch (error) {
          // Silencioso, no mostrar error (puede ser 404 si no existe el setting aún)
        }
      }
    };
    
    // Solo ejecutar si hay un ID válido
    if (record.id) {
      reloadTimezone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id, initialValue.id]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }

    onSubmit(data);
    // ✅ No usar initialValue aquí (está desactualizado hasta que el padre refresque).
    // Sincronizar con lo enviado para feedback inmediato; enableReinitialize aplicará datos del servidor cuando el padre actualice.
    setRecord((prev) => ({
      ...prev,
      ...data,
      dueDate:
        data.dueDate != null && data.dueDate !== ""
          ? moment(data.dueDate).format("YYYY-MM-DD")
          : "",
      planId: data.planId != null && data.planId !== "" ? String(data.planId) : "",
    }));
  };

  const handleOpenModalUsers = async () => {
    try {
      const { data } = await listUsersSimple({ companyId: initialValue.id });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e);
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate).add(1, "month").format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate).add(2, "month").format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate).add(3, "month").format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate).add(6, "month").format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate).add(12, "month").format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    setRecord(data);
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={initialValue.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      
      <Card className={classes.formCard}>
        <CardContent className={classes.formCardContent}>
          <Typography variant="h5" className={classes.formTitle}>
            <BusinessIcon />
            {record.id ? i18n.t("companies.formTitle.edit") : i18n.t("companies.formTitle.registerNew")}
          </Typography>
          
          <Formik
            enableReinitialize
            initialValues={record}
            key={`form-${record.id || 'new'}-${record.dueDate || 'no-date'}-${record.timezone || 'no-timezone'}`}
            onSubmit={(values) => {
              handleSubmit(values);
              // ✅ No resetForm tras guardar: resetForm() restauraba initialValues viejos y anulaba el cambio en pantalla.
            }}
          >
            {({ values, setFieldValue }) => {
              // ✅ Validar que planId existe en la lista de planes disponibles
              let validPlanId = values.planId || "";
              if (validPlanId && plans.length > 0) {
                const planExists = plans.some(p => String(p.id) === String(validPlanId));
                if (!planExists) {
                  // Si el plan no existe, limpiar el valor para evitar warning
                  validPlanId = "";
                  // Actualizar el valor en Formik si es diferente
                  if (values.planId !== validPlanId) {
                    setFieldValue('planId', validPlanId);
                  }
                }
              } else if (validPlanId && plans.length === 0) {
                // Si planes aún no se han cargado, limpiar temporalmente
                validPlanId = "";
              }
              
              // ✅ Validar que timezone existe en la lista de timezones disponibles
              // ✅ CORRECCIÓN: Mantener el valor actual aunque los timezones no se hayan cargado
              let validTimezone = values.timezone || "";
              
              // Solo validar si los timezones ya se cargaron
              if (validTimezone && !loadingTimezones && timezones.length > 0) {
                const timezoneExists = timezones.some(tz => tz.timezone === validTimezone);
                if (!timezoneExists) {
                  // Si el timezone no existe en la lista, usar el default
                  console.warn(`⚠️ Timezone "${validTimezone}" no existe en la lista, usando default`);
                  validTimezone = "America/Lima";
                  // Verificar que el default existe
                  const defaultExists = timezones.some(tz => tz.timezone === validTimezone);
                  if (!defaultExists && timezones.length > 0) {
                    // Si el default no existe, usar el primero disponible
                    validTimezone = timezones[0].timezone;
                  }
                  // Actualizar el valor en Formik si es diferente
                  if (values.timezone !== validTimezone) {
                    setFieldValue('timezone', validTimezone);
                  }
                }
              }
              // ✅ NO limpiar el timezone si aún se están cargando - mantener el valor actual
              // Esto permite que el dropdown muestre el valor correcto aunque los timezones no se hayan cargado
              
              return (
              <Form>
                <Grid container spacing={3} className={classes.formGrid}>
                  {/* Información Básica */}
                  <Grid item xs={12}>
                    <Typography variant="h6" className={classes.sectionTitle}>
                      <BusinessIcon />
                      {i18n.t("companies.sections.basicInfo")}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.fieldContainer}>
                      <TextField
                        label={i18n.t("companies.form.name")}
                        name="name"
                        value={values.name || ""}
                        onChange={(e) => setFieldValue('name', e.target.value)}
                        variant="outlined"
                        fullWidth
                        className={classes.textField}
                        InputProps={{
                          startAdornment: <BusinessIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                      />
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.fieldContainer}>
                      <TextField
                        label={i18n.t("companies.form.email")}
                        name="email"
                        value={values.email || ""}
                        onChange={(e) => setFieldValue('email', e.target.value)}
                        variant="outlined"
                        fullWidth
                        required
                        className={classes.textField}
                        InputProps={{
                          startAdornment: <EmailIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                      />
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.fieldContainer}>
                      <TextField
                        label={i18n.t("companies.form.phone")}
                        name="phone"
                        value={values.phone || ""}
                        onChange={(e) => setFieldValue('phone', e.target.value)}
                        variant="outlined"
                        fullWidth
                        className={classes.textField}
                        InputProps={{
                          startAdornment: <PhoneIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                      />
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.fieldContainer}>
                      <FormControl variant="outlined" fullWidth className={classes.selectField}>
                        <InputLabel>{i18n.t("companies.form.plan")}</InputLabel>
                        <Field
                          as={Select}
                          name="planId"
                          label={i18n.t("companies.form.plan")}
                          required
                          value={validPlanId}
                        >
                          {plans && plans.length > 0 ? (
                            plans.map((plan, key) => (
                              <MenuItem key={key} value={String(plan.id)}>
                                <StarIcon style={{ marginRight: 8 }} />
                                {plan.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled>
                              {i18n.t("companies.loadingPlans")}
                            </MenuItem>
                          )}
                        </Field>
                      </FormControl>
                    </div>
                  </Grid>

                  {/* Configuración */}
                  <Grid item xs={12}>
                    <Divider style={{ margin: "20px 0" }} />
                    <Typography variant="h6" className={classes.sectionTitle}>
                      <SettingsIcon />
                      {i18n.t("companies.sections.configuration")}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <div className={classes.fieldContainer}>
                      <FormControl variant="outlined" fullWidth className={classes.selectField}>
                        <InputLabel>{i18n.t("companies.form.status")}</InputLabel>
                        <Field
                          as={Select}
                          name="status"
                          label={i18n.t("companies.form.status")}
                        >
                          <MenuItem value={true}>
                            <CheckCircleIcon style={{ marginRight: 8, color: "#4CAF50" }} />
                            {i18n.t("companies.values.yes")}
                          </MenuItem>
                          <MenuItem value={false}>
                            <CancelIcon style={{ marginRight: 8, color: "#f44336" }} />
                            {i18n.t("companies.values.no")}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <div className={classes.fieldContainer}>
                      <FormControl variant="outlined" fullWidth className={classes.selectField}>
                        <InputLabel>{i18n.t("companies.form.campaigns")}</InputLabel>
                        <Field
                          as={Select}
                          name="campaignsEnabled"
                          label={i18n.t("companies.form.campaigns")}
                        >
                          <MenuItem value={true}>
                            <CheckCircleIcon style={{ marginRight: 8, color: "#2196F3" }} />
                            {i18n.t("companies.values.enabled")}
                          </MenuItem>
                          <MenuItem value={false}>
                            <CancelIcon style={{ marginRight: 8, color: "#757575" }} />
                            {i18n.t("companies.values.disabled")}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <div className={classes.fieldContainer}>
                      <Field
                        component={TextField}
                        label={i18n.t("companies.form.dueDate")}
                        type="date"
                        name="dueDate"
                        variant="outlined"
                        fullWidth
                        className={classes.textField}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: <CalendarIcon style={{ marginRight: 8, color: "#666" }} />
                        }}
                        onChange={(e) => setFieldValue('dueDate', e.target.value)}
                        value={values.dueDate || ""}
                      />
                    </div>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <div className={classes.fieldContainer}>
                      <FormControl variant="outlined" fullWidth className={classes.selectField}>
                        <InputLabel>{i18n.t("companies.form.recurrence")}</InputLabel>
                        <Field
                          as={Select}
                          name="recurrence"
                          label={i18n.t("companies.form.recurrence")}
                        >
                          <MenuItem value="MENSAL">{i18n.t("companies.values.monthly")}</MenuItem>
                          <MenuItem value="BIMESTRAL">{i18n.t("companies.values.bimonthly")}</MenuItem>
                          <MenuItem value="TRIMESTRAL">{i18n.t("companies.values.quarterly")}</MenuItem>
                          <MenuItem value="SEMESTRAL">{i18n.t("companies.values.semiannual")}</MenuItem>
                          <MenuItem value="ANUAL">{i18n.t("companies.values.annual")}</MenuItem>
                        </Field>
                      </FormControl>
                    </div>
                  </Grid>
                  
                  {/* ✅ Zona Horaria de la Empresa */}
                  <Grid item xs={12} md={6}>
                    <div className={classes.fieldContainer}>
                      <FormControl variant="outlined" fullWidth className={classes.selectField}>
                        <InputLabel>
                          <span role="img" aria-label={i18n.t("companies.timezone.ariaLabel")}>🌍</span>{" "}
                          {i18n.t("companies.timezone.label")}
                        </InputLabel>
                        <Field
                          as={Select}
                          name="timezone"
                          label={`🌍 ${i18n.t("companies.timezone.label")}`}
                          disabled={loadingTimezones}
                          value={validTimezone || ""}
                        >
                          {loadingTimezones ? (
                            <>
                              <MenuItem value="" disabled>{i18n.t("companies.timezone.loading")}</MenuItem>
                              {/* ✅ Mostrar el valor actual aunque se estén cargando */}
                              {validTimezone && (
                                <MenuItem value={validTimezone} disabled>
                                  {i18n.t("companies.timezone.loadingValue", { tz: validTimezone })}
                                </MenuItem>
                              )}
                            </>
                          ) : timezones.length > 0 ? (
                            // ✅ DB-FIRST: Renderizar dinámicamente desde la base de datos
                            timezones.map((tz) => (
                              <MenuItem key={tz.id || tz.timezone} value={tz.timezone}>
                                {tz.displayName}
                              </MenuItem>
                            ))
                          ) : (
                            // ✅ Fallback: Lista mínima si no hay datos + mostrar valor actual si existe
                            <>
                              {validTimezone && !timezones.some(tz => tz.timezone === validTimezone) && (
                                <MenuItem value={validTimezone}>
                                  {i18n.t("companies.timezone.fallbackCurrent", { tz: validTimezone })}
                                </MenuItem>
                              )}
                              <MenuItem value="America/Lima">Perú (America/Lima)</MenuItem>
                              <MenuItem value="America/Bogota">Colombia (America/Bogota)</MenuItem>
                              <MenuItem value="America/Mexico_City">México (America/Mexico_City)</MenuItem>
                              <MenuItem value="America/Argentina/Buenos_Aires">Argentina (America/Argentina/Buenos_Aires)</MenuItem>
                              <MenuItem value="America/Santiago">Chile (America/Santiago)</MenuItem>
                            </>
                          )}
                        </Field>
                      </FormControl>
                      <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                        {i18n.t("companies.timezone.hint")}
                      </Typography>
                    </div>
                  </Grid>
                </Grid>

                {/* Botones de Acción */}
                <div className={classes.buttonContainer}>
                  <Button
                    variant="contained"
                    className={`${classes.actionButton} ${classes.clearButton}`}
                    onClick={() => onCancel()}
                    startIcon={<ClearIcon />}
                    disabled={loading}
                  >
                    {i18n.t("companies.buttons.clear")}
                  </Button>
                  
                  {record.id !== undefined ? (
                    <>
                      <Button
                        variant="contained"
                        className={`${classes.actionButton} ${classes.secondaryButton}`}
                        onClick={() => onDelete(record)}
                        startIcon={<DeleteIcon />}
                        disabled={loading}
                      >
                        {i18n.t("companies.buttons.delete")}
                      </Button>
                      
                      <Button
                        variant="contained"
                        className={`${classes.actionButton} ${classes.primaryButton}`}
                        onClick={() => incrementDueDate()}
                        startIcon={<CalendarIcon />}
                        disabled={loading}
                      >
                        {i18n.t("companies.buttons.addDueDate")}
                      </Button>
                      
                      <Button
                        variant="contained"
                        className={`${classes.actionButton} ${classes.primaryButton}`}
                        onClick={() => handleOpenModalUsers()}
                        startIcon={<PersonIcon />}
                        disabled={loading}
                      >
                        {i18n.t("companies.buttons.user")}
                      </Button>
                    </>
                  ) : null}
                  
                  <Button
                    type="submit"
                    variant="contained"
                    className={`${classes.actionButton} ${classes.primaryButton}`}
                    startIcon={<SaveIcon />}
                    disabled={loading}
                  >
                    {loading ? i18n.t("companies.buttons.saving") : i18n.t("companies.buttons.save")}
                  </Button>
                </div>
              </Form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
}
